import { useId, useMemo } from 'react';
import { buildSigilKnot } from '@sorrem/shared';
import type { UISigilEnhancements } from '@sorrem-client/types';
import { sigilRunPath, splitSigilRuns, trimSigilRun, type SigilRun } from './sigil-runs';

interface SigilProps {
    /** The character id; the same id always draws the same knot. */
    readonly seed: string;
    readonly enhancements: UISigilEnhancements;
    /** Placement and size, as for any nested `<svg>` (CSS px, or user units inside another SVG). */
    readonly x?: number;
    readonly y?: number;
    readonly size: number;
    readonly className?: string;
}

/** The knot's outer radius inside the sigil's 260-unit box (centered on the origin). */
const KNOT_RADIUS = 90;
const MEDALLION_RADIUS = 104;
const CRAFT_RING_RADIUS = 113;
/** Samples trimmed from each end of an over-strand's outline, so it never notches the strand it continues. */
const OUTLINE_TRIM = 3;

const STEEL = '#9AA2B1';
const OUTLINE = '#0D0B18';
const GOLD = '#F0CF7A';
const VIOLET = '#A98BE8';
const SILVER = '#E6EAF0';

/** The strand's path at the sigil's knot radius. */
function runPath(run: SigilRun): string {
    return sigilRunPath(run, KNOT_RADIUS);
}

/** The strand's visible layers, drawn over its outline. Martial gold shows as rims along both edges of the steel. */
function Strand({ d, enhancements }: { d: string; enhancements: UISigilEnhancements }): JSX.Element {
    return (
        <>
            {enhancements.martialTier > 0 && (
                <path d={d} stroke={GOLD} strokeWidth={9} strokeOpacity={0.3 + 0.175 * enhancements.martialTier} strokeLinecap="round" />
            )}
            <path d={d} stroke={STEEL} strokeWidth={enhancements.martialTier > 0 ? 5.5 : 7} strokeLinecap="round" />
            {enhancements.craftingStage >= 1 && (
                <path d={d} stroke={SILVER} strokeWidth={4} strokeOpacity={0.18} strokeLinecap="round" />
            )}
        </>
    );
}

/**
 * A character's sigil: their seeded woven knot set like a jeweled medallion,
 * enhanced by their branch mastery. Martial etches gold into the steel, the
 * Veil kindles a violet glow, and Crafting adds silver work: a sheen, then a
 * ring, then ornament (docs/gameplay/skill-tree-ui.md, Sigil).
 */
export function Sigil({ seed, enhancements, x, y, size, className }: SigilProps): JSX.Element {
    const glowId = `sigil-glow-${useId().replace(/:/g, '')}`;
    const runs = useMemo(() => splitSigilRuns(buildSigilKnot(seed).points), [seed]);
    const glowOpacity = 0.12 + 0.07 * Math.min(10, enhancements.veilMilestones);
    const fullPath = useMemo(() => [...runs.under, ...runs.over].map(runPath).join(''), [runs]);

    return (
        <svg className={className} x={x} y={y} width={size} height={size} viewBox="-130 -130 260 260" fill="none">
            <defs>
                <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="9" />
                </filter>
            </defs>

            {/* The medallion the knot is set in. */}
            <circle r={MEDALLION_RADIUS} fill="#1B1730" stroke={STEEL} strokeOpacity={0.35} strokeWidth={2} />

            {/* Crafting silver work: a ring from Journeyman, beading from Master, engraving and accents at Artisan. */}
            {enhancements.craftingStage >= 2 && <circle r={CRAFT_RING_RADIUS} stroke={SILVER} strokeOpacity={0.75} strokeWidth={2} />}
            {enhancements.craftingStage >= 3 && (
                <circle r={CRAFT_RING_RADIUS} stroke={SILVER} strokeOpacity={0.85} strokeWidth={3.2} strokeDasharray="0 9.86" strokeLinecap="round" />
            )}
            {enhancements.craftingStage >= 4 && (
                <>
                    <circle r={CRAFT_RING_RADIUS - 5} stroke={SILVER} strokeOpacity={0.6} strokeWidth={0.8} strokeDasharray="6 4" />
                    {[0, 90, 180, 270].map((angle) => (
                        <path key={angle} d="M0 -124L4 -118L0 -112L-4 -118Z" fill={SILVER} transform={`rotate(${angle})`} />
                    ))}
                </>
            )}

            {/* The Veil's inner glow, behind the knot. */}
            <path d={fullPath} stroke={VIOLET} strokeWidth={16} strokeOpacity={glowOpacity} filter={`url(#${glowId})`} />

            {/* Under-strands first, then over-strands whose outline cuts the gap at each crossing. */}
            {runs.under.map((run, index) => (
                <path key={`under-outline-${index}`} d={runPath(run)} stroke={OUTLINE} strokeWidth={11} strokeLinecap="round" />
            ))}
            {runs.under.map((run, index) => (
                <Strand key={`under-${index}`} d={runPath(run)} enhancements={enhancements} />
            ))}
            {runs.over.map((run, index) => (
                <path
                    key={`over-outline-${index}`}
                    d={runPath(trimSigilRun(run, OUTLINE_TRIM))}
                    stroke={OUTLINE}
                    strokeWidth={11}
                    strokeLinecap="butt"
                />
            ))}
            {runs.over.map((run, index) => (
                <Strand key={`over-${index}`} d={runPath(run)} enhancements={enhancements} />
            ))}
        </svg>
    );
}
